import * as THREE from 'three';
import { SplatMesh, SparkRenderer } from '@sparkjsdev/spark';
import { DOJO_SPZ_URL, DOJO_SPZ_LOW_URL } from './config';

export const fitDojoSplatToScene = (dojoSplat) => {
    const bounds = dojoSplat.getBoundingBox(true);
    const center = new THREE.Vector3();
    const size   = new THREE.Vector3();
    bounds.getCenter(center);
    bounds.getSize(size);

    const maxDim   = Math.max(size.x, size.y, size.z) || 1;
    const fitScale = 12 / maxDim;

    dojoSplat.rotation.set(Math.PI, 0, 0);
    dojoSplat.scale.setScalar(fitScale);

    const centeredOffset = center.clone()
        .multiplyScalar(fitScale)
        .applyEuler(dojoSplat.rotation);
    dojoSplat.position.copy(centeredOffset.multiplyScalar(-1));
};

export const createSparkSystem = ({ renderer, scene, camera, isUnmountedRef }) => {
    const sparkRenderer = new SparkRenderer({
        renderer,
        sortRadial: false,
        autoUpdate: false,
        minSortIntervalMs: 120,
        enableLod: false,
    });
    sparkRenderer.frustumCulled = false;
    sparkRenderer.material.depthTest = true;
    scene.add(sparkRenderer);

    // Guard missing async sort targets during unmount
    const originalReadbackDepth = sparkRenderer.readbackDepth?.bind(sparkRenderer);
    if (originalReadbackDepth) {
        sparkRenderer.readbackDepth = async (args) => {
            if (isUnmountedRef.value || !args?.current?.target) return;
            try {
                return await originalReadbackDepth(args);
            } catch (err) {
                if (isUnmountedRef.value || err?.message === 'No target') return;
                throw err;
            }
        };
    }

    let activeDojoSplat  = null;
    let splatLoadProgress = 0;
    let lowSplat  = null;
    let highSplat = null;
    let highReady = false;

    const lastCameraPos  = new THREE.Vector3().copy(camera.position);
    const lastCameraQuat = new THREE.Quaternion().copy(camera.quaternion);
    let sparkNeedsUpdate   = true;
    let sparkUpdatePending = false;
    let lastSparkUpdateMs  = 0;

    const fitSplat = (splat) => {
        if (!splat) return false;
        try {
            const bounds = splat.getBoundingBox(true);
            if (!bounds || !isFinite(bounds.min.x) || bounds.isEmpty()) return false;
            fitDojoSplatToScene(splat);
            splat.visible = true;
            sparkNeedsUpdate = true;
            sparkRenderer.setDirty();
            return true;
        } catch (_) {
            return false;
        }
    };

    const loadSplat = async (url, { label, onReady }) => {
        try {
            const response = await fetch(url);
            if (isUnmountedRef.value) return null;
            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const contentLength = parseInt(response.headers.get('content-length') || '0', 10);
            const startMs = performance.now();

            const splat = new SplatMesh({
                stream: response.body,
                ...(contentLength > 0 ? { streamLength: contentLength } : {}),
                onProgress: (event) => {
                    if (isUnmountedRef.value) return;
                    if (label === 'high' && event.lengthComputable) {
                        splatLoadProgress = event.loaded / event.total;
                    }
                },
                onLoad: () => {
                    if (isUnmountedRef.value) return;
                    if (fitSplat(splat)) onReady?.(splat);
                    sparkRenderer.update({ scene, camera }).catch((err) => {
                        if (!isUnmountedRef.value) console.warn(`[Dojo:${label}] SparkRenderer update failed:`, err);
                    });
                    console.info(`[Dojo:${label}] loaded in ${Math.round(performance.now() - startMs)}ms`);
                },
            });

            splat.rotation.set(Math.PI, 0, 0);
            splat.visible = false;
            scene.add(splat);

            splat.initialized
                .then(() => {
                    if (!isUnmountedRef.value && fitSplat(splat)) onReady?.(splat);
                })
                .catch((err) => {
                    if (!isUnmountedRef.value) console.error(`[Dojo:${label}] SplatMesh init failed:`, err);
                });

            return splat;
        } catch (err) {
            if (!isUnmountedRef.value) console.error(`[Dojo:${label}] load failed:`, err);
            return null;
        }
    };

    const onHighReady = (splat) => {
        if (highReady) return;
        highReady = true;
        activeDojoSplat = splat;
        window.mySplat = splat;
        if (lowSplat) {
            scene.remove(lowSplat);
            try { lowSplat.dispose?.(); } catch (_) {}
            lowSplat = null;
        }
        splatLoadProgress = 1;
    };

    const onLowReady = (splat) => {
        if (highReady || isUnmountedRef.value) {
            scene.remove(splat);
            try { splat.dispose?.(); } catch (_) {}
            if (lowSplat === splat) lowSplat = null;
            return;
        }
        activeDojoSplat = splat;
        window.mySplat = splat;
    };

    const loadDojo = async () => {
        const highPromise = loadSplat(DOJO_SPZ_URL,     { label: 'high', onReady: onHighReady });
        const lowPromise  = loadSplat(DOJO_SPZ_LOW_URL, { label: 'low',  onReady: onLowReady  });
        lowSplat  = await lowPromise;
        highSplat = await highPromise;
    };

    const driveSparkUpdate = (qualitySparkUpdateIntervalMs) => {
        if (isUnmountedRef.value) return;
        if (sparkUpdatePending) return;

        const now   = performance.now();
        const moved = camera.position.distanceToSquared(lastCameraPos) > 1e-7 ||
            (1 - Math.abs(camera.quaternion.dot(lastCameraQuat))) > 1e-7;
        if (moved) sparkNeedsUpdate = true;

        if (!sparkNeedsUpdate && now - lastSparkUpdateMs < qualitySparkUpdateIntervalMs) return;

        sparkUpdatePending = true;
        sparkRenderer.update({ scene, camera })
            .catch((sparkErr) => {
                if (isUnmountedRef.value) return;
                const msg = String(sparkErr?.message || sparkErr || '');
                if (msg.includes('Worker terminate') || msg.includes('No target')) return;
                console.warn('Spark frame update failed:', sparkErr);
            })
            .finally(() => {
                lastSparkUpdateMs  = now;
                sparkNeedsUpdate   = false;
                lastCameraPos.copy(camera.position);
                lastCameraQuat.copy(camera.quaternion);
                sparkUpdatePending = false;
            });
    };

    const dispose = () => {
        for (const splat of [lowSplat, highSplat, activeDojoSplat]) {
            if (!splat) continue;
            scene.remove(splat);
            try { splat.dispose?.(); } catch (_) {}
        }
        lowSplat = highSplat = activeDojoSplat = null;
        sparkRenderer.autoUpdate = false;
        try { sparkRenderer.dispose(); } catch (err) {
            console.warn('SparkRenderer dispose warning:', err);
        }
        delete window.mySplat;
        delete window.mySparkRenderer;
    };

    const markDirty = () => { sparkNeedsUpdate = true; };

    return {
        sparkRenderer,
        loadDojo,
        driveSparkUpdate,
        markDirty,
        dispose,
        getLoadStats: () => ({
            loadProgress: splatLoadProgress,
            activeSplatCount: window.mySplat?.numSplats,
        }),
    };
};
